import React, { useState, type FC } from "react";
import Tooltip from "../Tooltip";
import Body from "../../atoms/typography/Body";
import { theme } from "../../../theme";
import IconButton from "../../atoms/buttons/IconButton";
import Modal from "../Modal";
import { Row } from "react-grid-system";
import { HorizontalSpacer } from "../../atoms/Spacers";
import ActionButton from "../../atoms/buttons/ActionButton";
import { EditIcon } from "../../atoms/icons/EditIcon";
import Alert from "../Alert";
import TextField from "../../atoms/inputs/TextField";
import BodySmall from "../../atoms/typography/BodySmall";
import { useNavigate } from "react-router-dom";
import RadioButtonBox from "../../atoms/inputs/RadioButtonBox";
import { useTranslation } from "react-i18next";
import { AddIcon } from "../../atoms/icons/AddIcon";
import type { InvoiceReaderTemplateType } from "../../../models/ClientInvoiceReaderTemplateModel";
import type { Language } from "../../../models/Language";
import { useCreateTemplate } from "../../../graphql/hooks/invoiceInterpretationTemplates/useCreateTemplate";

interface Props {
  clientIdentifier: string;
  fileId: string;
  disabled?: boolean;
}

const CreateNewTemplateButton: FC<Props> = ({
  clientIdentifier,
  fileId,
  disabled,
}) => {
  const naviagte = useNavigate();
  const { t } = useTranslation();

  const [modalOpen, setModalOpen] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [templateType, setTemplateType] = useState<
    undefined | InvoiceReaderTemplateType
  >(undefined);
  const [templateLanguage, setTemplateLanguage] = useState<
    Language | undefined
  >(undefined);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const onCompleteCreateTemplate = (result: any) => {
    if (result?.invoiceInterpretation?.createTemplate?.success === true) {
      setErrorMessage("");
      setSuccessMessage(t("documentHandler.templateHasBeenCreated"));
      setTemplateName("");
      setTemplateType(undefined);
    }
  };
  const onErrorCreateTemplate = () => {
    setErrorMessage(error?.toString() ?? t("errors.somethingWentWrong"));
  };
  const {
    createTemplate,
    loading: creatingTemplateLoading,
    error,
  } = useCreateTemplate({
    onCompleted: onCompleteCreateTemplate,
    onError: onErrorCreateTemplate,
  });
  const createNewTemplateFromFileId = async () => {
    if (!fileId) {
      setErrorMessage(t("documentHandler.noFileIdFound"));
      return;
    }
    if (!templateType || !templateType) {
      setErrorMessage(t("documentHandler.inputValidTemplateInfo"));
      return;
    }
    try {
      await createTemplate({
        variables: {
          clientIdentifier: clientIdentifier,
          input: {
            fileId: fileId,
            templateName: templateName ?? "New template",
            templateType: templateType,
            templateLanguage: templateLanguage,
          },
        },
      });
    } catch (e) {
      setErrorMessage(`${t("errors.somethingWentWrong")}, ${e?.toString()}`);
    }
  };

  const onClickCreateNewTemplateButton = (e: React.MouseEvent) => {
    e.stopPropagation();
    setModalOpen(true);
  };

  return (
    <div>
      <Tooltip
        content={
          <Body color={theme.colors.white}>
            {t("documentHandler.createNewTemplateFromDocument")}
          </Body>
        }
      >
        <IconButton
          icon={<AddIcon color={theme.colors.info} />}
          color={theme.colors.info20}
          onClick={onClickCreateNewTemplateButton}
          size="medium"
          disabled={disabled}
        />
      </Tooltip>

      {modalOpen && (
        <Modal
          onClose={() => setModalOpen(false)}
          closeIcon
          title={t("documentHandler.createNewTemplate")}
        >
          <TextField
            value={templateName}
            onChange={(value) => setTemplateName(value)}
            label={t("documentHandler.templateName")}
            placeholder={t("documentHandler.inputTemplateName")}
            fullWidth
          />

          <HorizontalSpacer spacing={2} />

          <BodySmall>{t("documentHandler.selectTemplateType")}</BodySmall>
          <HorizontalSpacer spacing={1} />
          <div>
            <div
              style={{
                display: "flex",
                gap: 24,
              }}
            >
              <RadioButtonBox
                title={t("documentHandler.credit")}
                checked={templateType === "CREDIT"}
                onChange={() => setTemplateType("CREDIT")}
              />
              <RadioButtonBox
                title={t("documentHandler.debit")}
                checked={templateType === "DEBIT"}
                onChange={() => setTemplateType("DEBIT")}
              />
              <RadioButtonBox
                title={t("documentHandler.debitMaskAndStampOnly")}
                checked={templateType === "DEBIT_MASK_AND_STAMP_ONLY"}
                onChange={() => setTemplateType("DEBIT_MASK_AND_STAMP_ONLY")}
              />
            </div>
            <HorizontalSpacer spacing={2} />
            <BodySmall>{t("random.selectLanguage")}</BodySmall>
            <HorizontalSpacer spacing={1} />
            <div
              style={{
                display: "flex",
                gap: 24,
              }}
            >
              <RadioButtonBox
                title={t("templates.swedish")}
                checked={templateLanguage === "SWEDISH"}
                onChange={() => setTemplateLanguage("SWEDISH")}
              />
              <RadioButtonBox
                title={t("templates.english")}
                checked={templateLanguage === "ENGLISH"}
                onChange={() => setTemplateLanguage("ENGLISH")}
              />
            </div>
          </div>
          <HorizontalSpacer spacing={1} />

          <HorizontalSpacer spacing={1} />

          {errorMessage ||
            (error && (
              <Alert
                type="error"
                title={t("documentHandler.couldNotCreateTemplate")}
                message={errorMessage || error?.toString()}
              />
            ))}

          {successMessage && (
            <Alert
              title={successMessage}
              type="success"
              message={t("documentHandler.clickBelowToEditTemplate")}
            />
          )}

          <HorizontalSpacer spacing={1} />

          <Row justify="center">
            {!successMessage ? (
              <ActionButton
                title={t("documentHandler.createNewTemplate")}
                loading={creatingTemplateLoading}
                onClick={createNewTemplateFromFileId}
                disabled={
                  !templateName ||
                  !templateType ||
                  creatingTemplateLoading ||
                  !templateLanguage
                }
                color={theme.colors.primary}
                icon={<AddIcon color={theme.colors.white} />}
              />
            ) : (
              <ActionButton
                title={t("documentHandler.clickHereToEditTemplate")}
                onClick={() =>
                  naviagte(`/client/${clientIdentifier}/invoicesNew/templates`)
                }
                color={theme.colors.primary}
                icon={<EditIcon color={theme.colors.white} />}
              />
            )}
          </Row>
        </Modal>
      )}
    </div>
  );
};

export default CreateNewTemplateButton;
